// src/components/StockManagement/StockManagementInModal.jsx
import React, { useState, useEffect } from 'react';
import { showNotification } from '../../utils/notification';
import stockManagementService from '../../services/stockManagementService';

const StockManagementInModal = ({
  isOpen, onClose, onSave, isSaving, inventories, categories
}) => {
  const [staging, setStaging] = useState([]);
  const [formData, setFormData] = useState({
    productId: '',
    qty: '',
    pricePerUnit: '',
    unitCost: '',
    categoryId: '',
    inventoryId: '',
    total: ''
  });

  // Cascading dropdown data
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [currentStockDetails, setCurrentStockDetails] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  useEffect(() => {
    if (isOpen) {
      console.log('========================================');
      console.log('=== STOCK IN MODAL OPENED ===');
      console.log('========================================');
      resetForm();
    }
  }, [isOpen, inventories, categories]);

  // Calculate total whenever qty or unitCost changes
  useEffect(() => {
    const qty = parseFloat(formData.qty) || 0;
    const cost = parseFloat(formData.unitCost) || 0;
    const total = qty * cost;
    setFormData(prev => ({ ...prev, total: formatCurrency(total) }));
  }, [formData.qty, formData.unitCost]);

  // When inventory changes, load its categories
  useEffect(() => {
    if (formData.inventoryId) {
      if (categories && categories.length > 0) {
        console.log('✅ Using categories from props:', categories);
        setAvailableCategories(categories);
      } else {
        loadCategoriesForInventory(formData.inventoryId);
      }
    } else {
      setAvailableCategories([]);
      setAvailableProducts([]);
      setFormData(prev => ({ ...prev, categoryId: '', productId: '' }));
    }
  }, [formData.inventoryId, categories]);

  // When category changes, load products for that category
  useEffect(() => {
    if (formData.categoryId) {
      loadProductsForCategory(formData.categoryId);
    } else {
      setAvailableProducts([]);
      setSelectedProductDetails(null);
      setFormData(prev => ({ ...prev, productId: '' }));
    }
  }, [formData.categoryId]);

  // When product changes, set its details from availableProducts
const loadCurrentStock = async (inventoryId, productId) => {
  try {
    setLoadingStock(true);
    console.log('🔍 Loading current stock for inventory:', inventoryId, 'product:', productId);
    const stockData = await stockManagementService.getStockByInventoryAndProduct(inventoryId, productId);
    console.log('✅ Current stock data:', stockData);
    
    // Store the full stock details
    const stockInfo = stockData?.[0] || null;
    setCurrentStockDetails(stockInfo);
  } catch (error) {
    console.error('❌ Failed to load current stock:', error);
    setCurrentStockDetails(null);
  } finally {
    setLoadingStock(false);
  }
};

// Update the useEffect to use currentStockDetails:
useEffect(() => {
  if (formData.productId && formData.inventoryId && availableProducts.length > 0) {
    const product = availableProducts.find(p => p.id == formData.productId);
    setSelectedProductDetails(product);
    loadCurrentStock(formData.inventoryId, formData.productId);
  } else {
    setSelectedProductDetails(null);
    setCurrentStockDetails(null);
  }
}, [formData.productId, formData.inventoryId, availableProducts]);

  const resetForm = () => {
    setStaging([]);
    setFormData({
      productId: '',
      qty: '',
      pricePerUnit: '',
      unitCost: '',
      categoryId: '',
      inventoryId: '',
      total: ''
    });
    setAvailableCategories([]);
    setAvailableProducts([]);
    setSelectedProductDetails(null);
  };

  const loadCategoriesForInventory = async (inventoryId) => {
    try {
      setLoadingCategories(true);
      if (categories && categories.length > 0) {
        setAvailableCategories(categories);
      } else {
        const cats = await stockManagementService.getActiveCategories();
        console.log('✅ Loaded categories:', cats);
        setAvailableCategories(cats);
      }
    } catch (error) {
      console.error('❌ Failed to load categories:', error);
      showNotification('Failed to load categories', 'error');
      setAvailableCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadProductsForCategory = async (categoryId) => {
    try {
      setLoadingProducts(true);
      console.log('🔍 Loading products for category:', categoryId);
      const products = await stockManagementService.getProductsByCategory(categoryId);
      console.log('✅ Raw API response:', products);
      console.log('📦 First product:', products?.[0]);
      console.log('📦 First product totalStock:', products?.[0]?.totalStock);
      console.log('📦 All product keys:', products?.[0] ? Object.keys(products[0]) : []);
      setAvailableProducts(products);
    } catch (error) {
      console.error('❌ Failed to load products:', error);
      showNotification('Failed to load products', 'error');
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(value).replace('LKR', 'Rs.');
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addToStaging = e => {
    e.preventDefault();
    if (!formData.productId || !formData.qty || !formData.unitCost || 
        !formData.pricePerUnit || !formData.categoryId || !formData.inventoryId) {
      showNotification('Please fill all required fields');
      return;
    }

    const selectedProduct = availableProducts.find(p => p.id == formData.productId);
    const selectedCategory = availableCategories.find(c => c.id == formData.categoryId);
    const selectedInventory = inventories.find(i => i.id == formData.inventoryId);

    const newItem = {
      productId: parseInt(formData.productId),
      productName: selectedProduct?.name || 'Unknown',
      qty: parseInt(formData.qty),
      pricePerUnit: parseFloat(formData.pricePerUnit),
      unitCost: parseFloat(formData.unitCost),
      categoryId: parseInt(formData.categoryId),
      categoryName: selectedCategory?.name || 'Unknown',
      inventoryId: parseInt(formData.inventoryId),
      inventoryName: selectedInventory?.name || 'Unknown'
    };

    setStaging(prev => [...prev, newItem]);
    setFormData(prev => ({
      ...prev,
      productId: '',
      qty: '',
      pricePerUnit: '',
      unitCost: '',
      total: ''
    }));
    setSelectedProductDetails(null);
  };

  const removeFromStaging = index => setStaging(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = () => {
    if (staging.length === 0) {
      showNotification('Please add at least one item');
      return;
    }

    const payload = {
      stocks: staging.map(item => ({
        productId: item.productId,
        categoryId: item.categoryId,
        inventoryId: item.inventoryId,
        qty: item.qty,
        unitCost: item.unitCost,
        pricePerUnit: item.pricePerUnit
      }))
    };

    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <header className="modal-header">
          <h3>Stock In</h3>
          <button className="close-modal-btn" onClick={onClose} disabled={isSaving}>×</button>
        </header>
        
        <div className="modal-body">
          <form onSubmit={addToStaging} className="modal-form">
            <div className="form-grid-single-column">
              
              <div className="form-row">
                <label htmlFor="si_to">To (Inventory) *</label>
                <select 
                  id="si_to" 
                  name="inventoryId"
                  className="custom-select" 
                  value={formData.inventoryId}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                >
                  <option value="">Select Inventory</option>
                  {inventories.map(inv => (
                    <option key={inv.id} value={inv.id}>{inv.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label htmlFor="si_category">Category *</label>
                <select 
                  id="si_category"
                  name="categoryId" 
                  className="custom-select"
                  value={formData.categoryId}
                  onChange={handleChange}
                  required
                  disabled={isSaving || !formData.inventoryId || loadingCategories}
                >
                  <option value="">
                    {!formData.inventoryId 
                      ? 'Select Inventory first' 
                      : loadingCategories 
                        ? 'Loading categories...' 
                        : 'Select Category'}
                  </option>
                  {availableCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label htmlFor="si_product">Product *</label>
                <select
                  id="si_product"
                  name="productId"
                  className="custom-select"
                  value={formData.productId}
                  onChange={handleChange}
                  required
                  disabled={isSaving || !formData.categoryId || loadingProducts}
                >
                  <option value="">
                    {!formData.categoryId 
                      ? 'Select Category first' 
                      : loadingProducts 
                        ? 'Loading products...' 
                        : 'Select Product'}
                  </option>
                  {availableProducts.map(prod => (
                    <option key={prod.id} value={prod.id}>{prod.name}</option>
                  ))}
                </select>
                 </div>
                <div className="form-row">
                  <label htmlFor="si_current_stock">Current Stock Info</label>
                  <input
                    type="text"
                    id="si_current_stock"
                    className="custom-input read-only"
                    value={
                      loadingStock 
                        ? 'Loading...' 
                        : currentStockDetails 
                          ? `Qty: ${currentStockDetails.qty} units | Unit Cost: Rs.${parseFloat(currentStockDetails.unitCost).toFixed(2)} | Price/Unit: Rs.${parseFloat(currentStockDetails.pricePerUnit).toFixed(2)}` 
                          : 'Select product first'
                    }
                    readOnly
                    style={{
                      color: currentStockDetails?.qty > 0 ? '#2563eb' : '#dc2626',
                      fontWeight: '500',
                      fontSize: '13px'
                    }}
                  />
                </div>
             

              <div className="form-row">
                <label htmlFor="si_qty">Quantity *</label>
                <input 
                  type="number" 
                  id="si_qty"
                  name="qty" 
                  className="custom-input"
                  value={formData.qty}
                  onChange={handleChange}
                  min="1"
                  required
                  disabled={isSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="si_unit_cost">Unit Cost *</label>
                <input 
                  type="number" 
                  id="si_unit_cost"
                  name="unitCost" 
                  className="custom-input"
                  value={formData.unitCost}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                  disabled={isSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="si_price_per_unit">Price per Unit *</label>
                <input 
                  type="number" 
                  id="si_price_per_unit"
                  name="pricePerUnit" 
                  className="custom-input"
                  value={formData.pricePerUnit}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                  disabled={isSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="si_total">Total Cost</label>
                <input 
                  type="text" 
                  id="si_total" 
                  className="custom-input read-only"
                  value={formData.total}
                  readOnly
                />
              </div>

            </div>
            
            <h4 className="mini-log-title">Batch Log: Items to Save</h4>
            <div className="mini-log-table-container">
              <table className="mini-log-table data-table">
                <thead>
                  <tr>
                    <th>Inventory</th>
                    <th>Category</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Unit Cost</th>
                    <th>Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {staging.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center' }}>
                        No items added yet.
                      </td>
                    </tr>
                  ) : (
                    staging.map((item, index) => (
                      <tr key={index}>
                        <td>{item.inventoryName}</td>
                        <td>{item.categoryName}</td>
                        <td>{item.productName}</td>
                        <td>{item.qty}</td>
                        <td>{formatCurrency(item.unitCost)}</td>
                        <td>{formatCurrency(item.pricePerUnit)}</td>
                        <td>
                          <button 
                            type="button"
                            className="btn-remove" 
                            onClick={() => removeFromStaging(index)}
                            disabled={isSaving}
                          >
                            X
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </form>
        </div>

        <footer className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose} disabled={isSaving}>
            Close
          </button>
          <button 
            className="btn btn-add" 
            onClick={addToStaging}
            disabled={isSaving}
          >
            Add to Batch
          </button>
          <button 
            className="btn btn-print" 
            onClick={handleSubmit}
            disabled={isSaving || staging.length === 0}
          >
            {isSaving ? 'Saving...' : 'Save Batch'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StockManagementInModal;